<template>
  <v-row class="inner-content">
    <v-col>

      <v-card class="pb-3">
        <v-card-title>
          Create keywords
          <a class="ml-3" style="font-size: 0.8em;" href="https://docs.google.com/spreadsheets/d/1uMxSNNB6zPTYNFIG3gOT5OBmoxm4E1-lCbq7knG1wCA/edit#gid=809749021" target="_blank"><v-icon>help</v-icon></a>
        </v-card-title>
        <v-card-text>

          <v-row>
            <v-col>
              <p>Create keywords by selecting an account and pasting keyword details into the field below. The corresponding negatives will also be created.</p>
            </v-col>
          </v-row>

          <v-form ref="form">

            <v-row>
              <v-col md="6">
                <GoogleAccountAutoComplete
                  v-model="selectedDestinationAccountID"
                  label="Destination account" 
                  @changed="destinationAccountChanged"
                ></GoogleAccountAutoComplete>
              </v-col>

              <v-col md="6">
                <GoogleAccountAutoComplete
                  v-model="selectedBaseAccountID"
                  label="Optional: base account. A KWR account should not be selected. Only the first keyword label found will be returned." 
                  :mandatory="false" 
                  @changed="baseAccountChanged"
                ></GoogleAccountAutoComplete>

                <GoogleCampaignAutoComplete 
                  v-model="selectedBaseCampaign"
                  label="Base account campaign"
                  :accountID="selectedBaseAccountID"
                  :mandatory="false"
                  :showAdGroupRefresh="true"
                  getParams="&amp;campaign_trial_type=BASE"
                ></GoogleCampaignAutoComplete>

                <v-btn style="min-width: 165px;" class="mt-4" color="success" :loading="fillingFromBase" :disabled="!selectedBaseCampaign" @click="fillFromBase()">Fill from base</v-btn>

              </v-col>
            </v-row>

            <v-row>
              <v-col md="12">
                <v-text-field style="font-size: 0.9em;" dense :placeholder="keywordsHeader" disabled></v-text-field>
                <v-textarea v-model="keywordsContent" clearable dense rows="10" hint="Paste keywords here. Columns should be TAB separated." style="background-color: lightyellow;"
                  required :rules="[v => !!v || 'Keywords are required']" @change="keywordsContentChanged">
                </v-textarea>
              </v-col>
            </v-row>

            <v-row>
              <v-col md="12">
                <v-btn color="success" :loading="validating" @click="create(true)">Validate</v-btn>
                <v-fade-transition mode="out-in">
                  <v-btn color="green darken-1" text v-show="showValid">Valid</v-btn>
                </v-fade-transition>
              </v-col>
            </v-row>

            <v-row>
              <v-col md="12">
                <v-alert v-model="showErrorAlert" color="error" border="left" elevation="2" colored-border dismissible class="mt-4 ml-2 mr-2" icon="error" style="white-space: pre-line;">{{errorMsg}}</v-alert>
              </v-col>
            </v-row>
          </v-form>

          <v-row>
            <v-col md="12" :hidden="!validated">
              <v-simple-table class="mt-4">
                <thead>
                  <tr class="pb-0">
                    <th>Campaign</th>
                    <th>Topic</th>
                    <th>Keyword</th>
                    <th>Status</th>
                    <th>Label</th>
                    <th>S.MatchType</th>
                    <th># existing ad groups</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in parsedKeywords" :key="item.campaign+item.topic+item.keyword">
                    <td>{{item.campaign}}</td>
                    <td>{{item.topic}}</td>
                    <td>{{item.keyword}}</td>
                    <td>{{item.status}}</td>
                    <td>{{item.label}}</td>
                    <td>{{item.match_type_segments_str}}</td>
                    <td>{{item.ad_group_count}}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <p class="mt-6" v-if="validated && additionalRowsText">{{additionalRowsText}}</p>
            </v-col>
          </v-row>

          <v-row>
            <v-col md="12" :hidden="!validated">
              <v-btn class="mt-0" color="primary" :loading="addingJob" @click="create(false)">Add creation job</v-btn>
              <v-fade-transition mode="out-in">
                <v-btn color="primary darken-1" text v-show="showJobAdded">Added</v-btn>
              </v-fade-transition>
            </v-col>
          </v-row>

        </v-card-text>

        <h4 class="mb-2 mt-4 ml-3">Keyword creation jobs
          <v-btn v-show="!showJobs" icon @click="toggleShowJobs(true)">
            <v-icon small>visibility</v-icon>
          </v-btn>
          <v-btn v-show="showJobs" icon @click="toggleShowJobs(false)">
            <v-icon small>visibility_off</v-icon>
          </v-btn>
        </h4>

        <v-row class="mr-3">
          <v-col>
            <SystemJobsTable v-show="showJobs" :headers="jobHeaders" :jobType="jobType" :updateKey="updateTableKey"></SystemJobsTable>
          </v-col>
        </v-row>
      </v-card>

    </v-col>
  </v-row>

</template>

<script>
import ccMixin from '@/mixins/contentCreator'
import formsMixin from '@/mixins/forms'

export default {
  name: 'GoogleKeywords',

  data () {
    return {
      keywordsContent: '',
      parsedKeywords: [],
      additionalRowsText: '',
      keywordsHeader: 'Campaign   Topic   Keyword   Status   Label   S.MatchType',
      selectedDestinationAccountID: 0,
      selectedBaseAccountID: 0,
      selectedBaseCampaign: null,
      validating: false,
      addingJob: false,
      fillingFromBase: false,
      lsSelectedDestinationAccount: 'contentCreator_selectedDestinationAccount',
      lsSelectedBaseAccount: 'contentCreator_selectedBaseAccount',
      lsContent: 'contentCreator_keywordsContent',
      lsShowJobs: 'contentCreator_showJobs',
      validated: false,
      showValid: false,
      showJobAdded: false,
      showErrorAlert: false,
      errorMsg: '',
      showJobs: false,
      updateTableKey: 'init',
      jobType: 'Adwords - create keywords content',
      jobHeaders: [
        { text: 'User', value: 'user_name' },
        { text: 'Status', value: 'status' },
        { text: 'Account', value: 'text1' },
        { text: '# keywords', value: 'text2' },
        { text: 'Entry at', value: 'entry_at_time' },
        { text: 'Completed at', value: 'completed_at_time' },
        { text: 'External message', value: 'external_message' },
      ],
    }
  },

  mixins: [ccMixin, formsMixin],

  computed: {
    baseEndpoint () {
      return '/a/google/accounts/' + this.selectedBaseAccountID + '/campaigns/' + this.selectedBaseCampaign.campaign_id + '/content-creator/keywords'
    },

    destEndpoint () {
      return '/a/google/accounts/' + this.selectedDestinationAccountID + '/content-creator/keywords'
    },
  },

  components: {
    GoogleAccountAutoComplete: () => import('@/components/autocompletes/GoogleAccountAutoComplete.vue'),
    GoogleCampaignAutoComplete: () => import('@/components/autocompletes/GoogleCampaignAutoComplete.vue'),
    SystemJobsTable: () => import('@/components/tables/SystemJobsTable.vue'),
  },

  methods: {
    baseAccountChanged: function () {
      if (!this.selectedBaseAccountID) {
        this.selectedBaseAccountID = 0
      }

      this.selectedBaseCampaign = null

      localStorage.setItem(this.lsSelectedBaseAccount, JSON.stringify(this.selectedBaseAccountID))
    },

    create (validateOnly = true) {
      this.showErrorAlert = false
      this.additionalRowsText = ''
      if (!this.$refs.form.validate()) {
        return false
      }

      var action = 'addingJob'
      var showVar = 'showJobAdded'
      if (validateOnly) {
        this.validated = false
        action = 'validating'
        showVar = 'showValid'
      } else {
        this.toggleShowJobs(true)
      }

      this[action] = true

      var splitLines = this.keywordsContent.split('\n')
      var obj = {
        'lines': splitLines,
        'validate_only': validateOnly
      }
      var body = JSON.stringify(obj)

      this.$http.post(this.destEndpoint, body).then(resp => {
        if (resp.data.data.error_msgs) {
          this.errorMsg = this.forms_getValidationDisplay(resp.data.data.error_msgs, 'error(s)')
          this.showErrorAlert = true
          this.parsedKeywords = []
        } else {
          if (resp.data.data.parsed_keywords) {
            const maxTableRecs = 5
            if (resp.data.data.parsed_keywords.length > maxTableRecs) {
              this.parsedKeywords = resp.data.data.parsed_keywords.slice(0, maxTableRecs)
              this.additionalRowsText = 'Showing first ' + maxTableRecs + ' records. A further ' + (resp.data.data.parsed_keywords.length - maxTableRecs) + ' records are not shown.'
            } else {
              this.parsedKeywords = resp.data.data.parsed_keywords
            }
            this.updateTableKey = this.$dateTime.now().toString()
            this.validated = true
            this[showVar] = true
            this.timer = setTimeout(() => { this[showVar] = false }, this.cc_buttonDisplayMS)
          } else {
            this.parsedKeywords = []
          }
        }
      })
      .catch(errResp => {
        this.validated = false
        this.$store.commit('system/setAPIError', { method: 'POST', url: this.destEndpoint, description: errResp.response.data.err_description, ext_msg: errResp.response.data.external_message })
      })
      .finally(() => this[action] = false)
    },

    destinationAccountChanged: function () {
      localStorage.setItem(this.lsSelectedDestinationAccount, JSON.stringify(this.selectedDestinationAccountID))
    },

    fillFromBase: function () {
      if (this.selectedBaseAccountID == 0 || !this.selectedBaseCampaign) {
        return
      }

      this.keywordsContent = ''
      this.validated = false
      this.showErrorAlert = false
      this.fillingFromBase = true

      // fetch DB data for selected campaign
      var myURL = this.baseEndpoint

      this.$http.get(myURL).then(resp => {
        if (resp.data.data) {

          // put the DB data into tab-separated format
          var content = ''
          resp.data.data.forEach(v => {
            content += v.campaign
            content += '\t' + v.topic
            content += '\t' + v.keyword
            content += '\t' + v.status
            content += '\t' + v.label
            content += '\t' + v.match_type_segments_str
            content += '\n'
          })
          if (content) {
            this.keywordsContent = content
          } else {
            this.keywordsContent = 'Keywords unavailable because ad groups for this account either do not exist, or have not yet been downloaded. Please refresh the base account ad groups above, and then try again.'
          }
        }
      })
      .catch(errResp => {
        this.$store.commit('system/setAPIError', { method: 'GET', url: myURL, description: errResp.response.data.err_description, ext_msg: errResp.response.data.external_message })
      })
      .finally(() => this.fillingFromBase = false)
    },

    keywordsContentChanged: function () {
      this.validated = false
      this.showValid = false
      this.showErrorAlert = false

      // save to local storage
      localStorage.setItem(this.lsContent, JSON.stringify(this.keywordsContent))
    },

    toggleShowJobs (show) {
      this.showJobs = show

      // save to local storage
      localStorage.setItem(this.lsShowJobs, JSON.stringify(this.showJobs))
    }
  },

  created: function () {
    // if present, use local selected accounts
    if (localStorage.getItem(this.lsSelectedDestinationAccount)) {
      this.selectedDestinationAccountID = JSON.parse(localStorage.getItem(this.lsSelectedDestinationAccount))
      this.destinationAccountChanged()
    }
    if (localStorage.getItem(this.lsSelectedBaseAccount)) {
      this.selectedBaseAccountID = JSON.parse(localStorage.getItem(this.lsSelectedBaseAccount))
      this.baseAccountChanged()
    }

    // if present, use content
    if (localStorage.getItem(this.lsContent)) {
      this.keywordsContent = JSON.parse(localStorage.getItem(this.lsContent))
    }

    // if present, use showJobs
    if (localStorage.getItem(this.lsShowJobs)) {
      this.showJobs = JSON.parse(localStorage.getItem(this.lsShowJobs))
    }
  },

}
</script>

<style scoped>
</style>